import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import { IconUpload } from "@tabler/icons-react";
import {
  asyncAutocompleteListboxProps,
  buildCustomerSearchUrl,
  handleAsyncAutocompleteInputChange,
  noLocalFilter,
  useAsyncEntitySearch,
} from "utils/entity-autocomplete";
import { ACCEPTED_ATTENDANCE_FILES, formatCpf } from "utils/attendance";

const emptyForm = {
  customer: null,
  contract: null,
  subject: "",
  description: "",
};

const AttendanceFormModal = ({
  open,
  onClose,
  onSaved,
  subjects = [],
  initialCustomer = null,
  lockCustomer = false,
}) => {
  const [form, setForm] = useState(emptyForm);
  const [contracts, setContracts] = useState([]);
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [customerInput, setCustomerInput] = useState("");
  const initialCustomerRef = useRef(initialCustomer);
  initialCustomerRef.current = initialCustomer;

  const {
    options: customerOptions,
    loading: customersLoading,
    search: searchCustomers,
    listboxKey,
  } = useAsyncEntitySearch({
    buildUrl: buildCustomerSearchUrl,
    selectedEntity: form.customer,
  });

  useEffect(() => {
    if (!open) return;
    const preset = initialCustomerRef.current;
    const customer = preset?.id ? preset : null;
    setForm({ ...emptyForm, customer });
    setFiles([]);
    setError("");
    setFieldErrors({});
    setCustomerInput("");
    setContracts([]);
  }, [open]);

  useEffect(() => {
    const customerId = form.customer?.id;
    if (!customerId) {
      setContracts([]);
      return;
    }
    let cancelled = false;
    fetch(`/backend/contracts/?customer_id=${customerId}&page_size=100`)
      .then((response) => (response.ok ? response.json() : Promise.reject()))
      .then((data) => {
        if (!cancelled) setContracts(data.results || []);
      })
      .catch(() => {
        if (!cancelled) setContracts([]);
      });
    return () => {
      cancelled = true;
    };
  }, [form.customer?.id]);

  const handleSave = async () => {
    setError("");
    const errors = {};
    if (!form.customer?.id) errors.customer = "Selecione um cliente cadastrado.";
    if (!form.subject) errors.subject = "Selecione o motivo do contato.";
    if (!form.description.trim()) errors.description = "Informe a descrição da primeira interação.";
    setFieldErrors(errors);
    if (Object.keys(errors).length) return;

    const data = new FormData();
    data.append("customer", String(form.customer.id));
    if (form.contract?.id) data.append("contract", String(form.contract.id));
    data.append("subject", form.subject);
    data.append("description", form.description.trim());
    files.forEach((file) => data.append("files", file));

    setSaving(true);
    try {
      const response = await fetch("/backend/attendances/", {
        method: "POST",
        body: data,
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        const nextErrors = {};
        ["customer", "contract", "subject", "description", "files"].forEach((field) => {
          if (payload[field]) {
            nextErrors[field] = Array.isArray(payload[field]) ? payload[field][0] : payload[field];
          }
        });
        setFieldErrors(nextErrors);
        throw new Error(payload.detail || payload.error || payload.files?.[0] || "Não foi possível salvar o atendimento.");
      }
      onSaved(payload);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Novo atendimento</DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 0.5 }}>
          <Autocomplete
            options={customerOptions}
            getOptionLabel={(option) => {
              const cpf = formatCpf(option?.cpf);
              return option?.cpf ? `${option.name} — ${cpf}` : option?.name || "";
            }}
            value={form.customer}
            disabled={lockCustomer}
            onChange={(event, newValue) => {
              if (lockCustomer) return;
              setForm((prev) => ({ ...prev, customer: newValue, contract: null }));
              if (fieldErrors.customer) setFieldErrors((prev) => ({ ...prev, customer: null }));
            }}
            onInputChange={(event, newInputValue, reason) => {
              setCustomerInput(newInputValue);
              handleAsyncAutocompleteInputChange(searchCustomers, reason, newInputValue);
            }}
            onOpen={() => searchCustomers(customerInput.trim())}
            loading={customersLoading}
            filterOptions={noLocalFilter}
            ListboxProps={asyncAutocompleteListboxProps(listboxKey)}
            isOptionEqualToValue={(option, value) => Number(option?.id) === Number(value?.id)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Cliente *"
                error={!!fieldErrors.customer}
                helperText={
                  fieldErrors.customer ||
                  (lockCustomer
                    ? "Cliente definido pela visão do cliente."
                    : "Pesquise por nome ou CPF. Não é possível cadastrar cliente aqui.")
                }
              />
            )}
          />

          <Autocomplete
            options={contracts}
            getOptionLabel={(option) =>
              option.process_number || option.number || `Contrato #${option.id}`
            }
            value={form.contract}
            onChange={(event, newValue) => setForm((prev) => ({ ...prev, contract: newValue }))}
            disabled={!form.customer}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Processo"
                error={!!fieldErrors.contract}
                helperText={fieldErrors.contract || "Opcional. Exibe os processos do cliente selecionado."}
              />
            )}
            isOptionEqualToValue={(option, value) => Number(option?.id) === Number(value?.id)}
          />

          <TextField
            select
            SelectProps={{ native: true }}
            label="Motivo do contato *"
            value={form.subject}
            onChange={(e) => {
              setForm((prev) => ({ ...prev, subject: e.target.value }));
              if (fieldErrors.subject) setFieldErrors((prev) => ({ ...prev, subject: null }));
            }}
            error={!!fieldErrors.subject}
            helperText={fieldErrors.subject || ""}
          >
            <option value=""></option>
            {subjects.map((subject) => (
              <option key={subject.value} value={subject.value}>
                {subject.label}
              </option>
            ))}
          </TextField>

          <TextField
            label="Descrição da primeira interação *"
            value={form.description}
            onChange={(e) => {
              setForm((prev) => ({ ...prev, description: e.target.value }));
              if (fieldErrors.description) setFieldErrors((prev) => ({ ...prev, description: null }));
            }}
            multiline
            minRows={4}
            error={!!fieldErrors.description}
            helperText={fieldErrors.description || ""}
          />

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, display: "flex", alignItems: "center", gap: 0.5 }}>
              <IconUpload stroke={1.5} size="1.1rem" />
              Anexos (opcional)
            </Typography>
            <Button variant="outlined" component="label" size="small">
              Selecionar arquivos
              <input
                type="file"
                hidden
                multiple
                accept={ACCEPTED_ATTENDANCE_FILES}
                onChange={(e) => setFiles(Array.from(e.target.files || []))}
              />
            </Button>
            {files.length > 0 && (
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                {files.map((file) => file.name).join(", ")}
              </Typography>
            )}
            {fieldErrors.files && (
              <Typography variant="caption" color="error" display="block" sx={{ mt: 1 }}>
                {fieldErrors.files}
              </Typography>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" disabled={saving}>
          Cancelar
        </Button>
        <Button onClick={handleSave} color="primary" disabled={saving}>
          {saving ? "Salvando..." : "Salvar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AttendanceFormModal;
